import { BED_JOINT_GAP_MM, EPSILON, formatMm } from '@bw/core';
import type { BrickPattern, CornerSnap, Measurement, Mm, SnapResult, WallModel } from '@bw/core';
import type { Viewport } from './viewport.js';
import { VIEWPORT_OVERSCAN_PX } from './viewport.js';
import type { DrawTheme } from './theme.js';
import { lighten } from './theme.js';

const DEFAULT_PREVIEW_FILL = '#B5523B';

function label(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  colour: string,
): void {
  ctx.save();
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = colour;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * Diagonal hatch over an invalid preview. Validity must not rest on colour
 * alone (design document C2).
 */
function hatch(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  colour: string,
): void {
  if (w <= 2 || h <= 2) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.strokeStyle = colour;
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let offset = -h; offset < w; offset += 7) {
    ctx.moveTo(x + offset, y + h);
    ctx.lineTo(x + offset + h, y);
  }
  ctx.stroke();
  ctx.restore();
}

/** Clause 12.2 — the build preview. */
export function paintBuildPreview(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  snap: SnapResult,
  width: Mm,
  height: Mm,
  valid: boolean,
  caption: string,
): void {
  const left = viewport.toScreenX(snap.x);
  const top = viewport.toScreenY(snap.y + height);
  const w = width * viewport.scale;
  const h = height * viewport.scale;
  const outline = valid ? theme.previewValid : theme.previewInvalid;

  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = lighten(DEFAULT_PREVIEW_FILL, 0.2);
  ctx.fillRect(left, top, w, h);
  ctx.restore();
  if (!valid) hatch(ctx, left, top, w, h, outline);

  ctx.save();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 3;
  ctx.setLineDash([7, 4]);
  ctx.strokeRect(left, top, w, h);
  ctx.restore();

  label(ctx, caption, left + w / 2, top - 8, outline);
}

/** Clause 12.4 — the paste preview. */
export function paintPastePreview(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  snap: SnapResult,
  pattern: BrickPattern,
  valid: boolean,
  caption: string,
  colourOf: (materialId: string) => string,
): void {
  const outline = valid ? theme.previewValid : theme.previewInvalid;
  const view = viewport.visibleBounds(VIEWPORT_OVERSCAN_PX);

  ctx.save();
  ctx.setLineDash([6, 3]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = outline;
  for (const entry of pattern.bricks) {
    const x = snap.x + entry.dx;
    const y = snap.y + entry.dy;
    if (
      x + entry.width < view.minX - EPSILON ||
      x > view.maxX + EPSILON ||
      y + entry.height < view.minY - EPSILON ||
      y > view.maxY + EPSILON
    ) {
      continue;
    }
    const left = viewport.toScreenX(x);
    const top = viewport.toScreenY(y + entry.height);
    const w = entry.width * viewport.scale;
    const h = entry.height * viewport.scale;
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = lighten(colourOf(entry.materialId), 0.16);
    ctx.fillRect(left, top, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeRect(left, top, w, h);
  }
  ctx.restore();

  const left = viewport.toScreenX(snap.x);
  const top = viewport.toScreenY(snap.y + pattern.height);
  const w = pattern.width * viewport.scale;
  const h = pattern.height * viewport.scale;
  if (!valid) hatch(ctx, left, top, w, h, outline);
  ctx.save();
  ctx.setLineDash([3, 4]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = outline;
  ctx.strokeRect(left, top, w, h);
  ctx.restore();
  label(ctx, caption, left + w / 2, top - 8, outline);
}

/** Clause 12.3 — snap guides. */
export function paintSnapGuide(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  model: WallModel,
  snap: SnapResult,
  previewWidth: Mm,
  previewHeight: Mm,
): void {
  if (snap.relation === null) return;
  ctx.save();
  ctx.strokeStyle = theme.snapGuide;
  ctx.fillStyle = theme.snapGuide;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.font = 'bold 11px system-ui, sans-serif';

  if (snap.relation === 'baseline' || snap.relation === 'axes') {
    const sy = viewport.toScreenY(0);
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(viewport.width, sy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillText('X-axis', 9, sy - 6);
    ctx.setLineDash([4, 4]);
  }
  if (snap.relation === 'y-axis' || snap.relation === 'axes') {
    const sx = viewport.toScreenX(0);
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, viewport.height);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillText('Y-axis', sx + 6, 18);
    ctx.setLineDash([4, 4]);
  }
  if (snap.relation === 'baseline' || snap.relation === 'y-axis' || snap.relation === 'axes') {
    ctx.restore();
    return;
  }

  const brick = model.get(snap.referenceId);
  if (brick === undefined) {
    ctx.restore();
    return;
  }

  if (snap.relation === 'above' || snap.relation === 'below') {
    const gapY =
      snap.relation === 'above'
        ? brick.top + BED_JOINT_GAP_MM / 2
        : brick.y - BED_JOINT_GAP_MM / 2;
    const sy = viewport.toScreenY(gapY);
    const x1 = viewport.toScreenX(Math.min(brick.x, snap.x));
    const x2 = viewport.toScreenX(Math.max(brick.right, snap.x + previewWidth));
    ctx.beginPath();
    ctx.moveTo(x1, sy);
    ctx.lineTo(x2, sy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.textAlign = 'center';
    ctx.fillText(`${formatMm(BED_JOINT_GAP_MM)} mm`, (x1 + x2) / 2, sy - 6);
  } else {
    const gapX =
      snap.relation === 'right'
        ? brick.right + model.perpendGapMm / 2
        : brick.x - model.perpendGapMm / 2;
    const sx = viewport.toScreenX(gapX);
    const y1 = viewport.toScreenY(Math.max(brick.top, snap.y + previewHeight));
    const y2 = viewport.toScreenY(Math.min(brick.y, snap.y));
    ctx.beginPath();
    ctx.moveTo(sx, y1);
    ctx.lineTo(sx, y2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.textAlign = 'left';
    ctx.fillText(`${formatMm(model.perpendGapMm)} mm`, sx + 6, (y1 + y2) / 2);
  }
  ctx.restore();
}

/** Clause 12.5 — the selection marquee, in screen space. */
export function paintMarquee(
  ctx: CanvasRenderingContext2D,
  theme: DrawTheme,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): void {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const w = Math.abs(x2 - x1);
  const h = Math.abs(y2 - y1);
  ctx.save();
  ctx.fillStyle = theme.marqueeFill;
  ctx.fillRect(left, top, w, h);
  ctx.strokeStyle = theme.marqueeStroke;
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 3]);
  ctx.strokeRect(left, top, w, h);
  ctx.restore();
}

function marker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  colour: string,
  radius: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

export function measurementText(measurement: Measurement): string[] {
  return [
    `X ${formatMm(measurement.xDistance)} mm   Y ${formatMm(measurement.yDistance)} mm`,
    `Absolute distance ${measurement.roundedAbsoluteDistance} mm`,
    `Angle from horizontal ${measurement.roundedAngleFromHorizontal.toFixed(1)}°`,
  ];
}

/** Clause 12.6 — the measurement overlay. */
export function paintMeasurement(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  measurement: Measurement | null,
  start: CornerSnap | null,
  hover: CornerSnap | null,
): void {
  if (measurement !== null) {
    const sx = viewport.toScreenX(measurement.start.x);
    const sy = viewport.toScreenY(measurement.start.y);
    const ex = viewport.toScreenX(measurement.end.x);
    const ey = viewport.toScreenY(measurement.end.y);

    ctx.save();
    ctx.strokeStyle = theme.measureGuide;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, sy);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.restore();

    if (Math.hypot(ex - sx, ey - sy) > EPSILON) {
      ctx.save();
      ctx.strokeStyle = theme.measureLine;
      ctx.fillStyle = theme.measureLine;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      const angle = Math.atan2(ey - sy, ex - sx);
      for (const [hx, hy, dir] of [
        [ex, ey, angle],
        [sx, sy, angle + Math.PI],
      ] as const) {
        ctx.beginPath();
        ctx.moveTo(hx, hy);
        ctx.lineTo(hx - 10 * Math.cos(dir - 0.38), hy - 10 * Math.sin(dir - 0.38));
        ctx.lineTo(hx - 8 * Math.cos(dir), hy - 8 * Math.sin(dir));
        ctx.lineTo(hx - 10 * Math.cos(dir + 0.38), hy - 10 * Math.sin(dir + 0.38));
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }

    const lines = measurementText(measurement);
    ctx.save();
    ctx.font = 'bold 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    const widest = Math.max(...lines.map((line) => ctx.measureText(line).width));
    const lineHeight = 15;
    const boxW = widest + 14;
    const boxH = lines.length * lineHeight + 10;
    const cx = (sx + ex) / 2;
    const cy = (sy + ey) / 2 - 30;
    ctx.fillStyle = theme.measureLabelBg;
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
    ctx.strokeRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
    ctx.fillStyle = theme.measureLabelFg;
    lines.forEach((line, index) => {
      ctx.fillText(line, cx, cy - boxH / 2 + 15 + index * lineHeight);
    });
    ctx.restore();

    marker(ctx, sx, sy, theme.measureLine, 5);
    marker(ctx, ex, ey, theme.measureLine, 5);
  } else if (start !== null) {
    marker(ctx, viewport.toScreenX(start.x), viewport.toScreenY(start.y), theme.measureLine, 5);
  }

  if (hover !== null) {
    marker(ctx, viewport.toScreenX(hover.x), viewport.toScreenY(hover.y), theme.measureHover, 7);
  }
}

export interface GeneratePreview {
  readonly envelope: { readonly x: Mm; readonly y: Mm; readonly w: Mm; readonly h: Mm };
  readonly placeable: readonly { readonly x: Mm; readonly y: Mm; readonly width: Mm; readonly height: Mm }[];
  readonly clashing: readonly { readonly x: Mm; readonly y: Mm; readonly width: Mm; readonly height: Mm }[];
  readonly colour: string;
  readonly caption: string;
}

/**
 * Design document C4 — nothing is written until the user confirms, so the
 * canvas shows exactly what will be laid, and marks what will be skipped.
 */
export function paintGeneratePreview(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  preview: GeneratePreview,
): void {
  const view = viewport.visibleBounds(VIEWPORT_OVERSCAN_PX);
  const onScreen = (x: Mm, y: Mm, w: Mm, h: Mm): boolean =>
    x + w >= view.minX && x <= view.maxX && y + h >= view.minY && y <= view.maxY;

  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = lighten(preview.colour, 0.18);
  ctx.strokeStyle = theme.previewValid;
  ctx.lineWidth = 1;
  for (const brick of preview.placeable) {
    if (!onScreen(brick.x, brick.y, brick.width, brick.height)) continue;
    const left = viewport.toScreenX(brick.x);
    const top = viewport.toScreenY(brick.y + brick.height);
    ctx.fillRect(left, top, brick.width * viewport.scale, brick.height * viewport.scale);
  }
  ctx.restore();

  // Skipped positions are marked, not hidden: a bond generated around
  // existing work should show where it gave way.
  ctx.save();
  ctx.strokeStyle = theme.previewInvalid;
  ctx.lineWidth = 1.5;
  for (const brick of preview.clashing) {
    if (!onScreen(brick.x, brick.y, brick.width, brick.height)) continue;
    const left = viewport.toScreenX(brick.x);
    const top = viewport.toScreenY(brick.y + brick.height);
    const w = brick.width * viewport.scale;
    const h = brick.height * viewport.scale;
    ctx.beginPath();
    ctx.moveTo(left, top);
    ctx.lineTo(left + w, top + h);
    ctx.moveTo(left + w, top);
    ctx.lineTo(left, top + h);
    ctx.stroke();
  }
  ctx.restore();

  const left = viewport.toScreenX(preview.envelope.x);
  const top = viewport.toScreenY(preview.envelope.y + preview.envelope.h);
  const w = preview.envelope.w * viewport.scale;
  const h = preview.envelope.h * viewport.scale;
  ctx.save();
  ctx.strokeStyle = theme.snapGuide;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 5]);
  ctx.strokeRect(left, top, w, h);
  ctx.restore();

  // Live dimensions on two sides, as an envelope is dragged.
  ctx.save();
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.fillStyle = theme.snapGuide;
  ctx.textAlign = 'center';
  ctx.fillText(`${formatMm(preview.envelope.w)} mm`, left + w / 2, top + h + 15);
  ctx.save();
  ctx.translate(left - 8, top + h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`${formatMm(preview.envelope.h)} mm`, 0, 0);
  ctx.restore();
  ctx.restore();

  label(ctx, preview.caption, left + w / 2, top - 8, theme.previewValid);
}

/**
 * Design document C8 — the keyboard cursor.
 *
 * It used to be visible only as a brick preview, and only in build mode, so in
 * any other tool the arrow keys moved something nobody could see.
 */
export function paintKeyboardCursor(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
  cursor: { readonly x: Mm; readonly y: Mm },
): void {
  const x = viewport.toScreenX(cursor.x);
  const y = viewport.toScreenY(cursor.y);
  const arm = 11;
  ctx.save();
  ctx.strokeStyle = theme.snapGuide;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(x - arm, y);
  ctx.lineTo(x + arm, y);
  ctx.moveTo(x, y - arm);
  ctx.lineTo(x, y + arm);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillStyle = theme.snapGuide;
  ctx.fillText(`X ${formatMm(cursor.x)}  Y ${formatMm(cursor.y)}`, x + arm + 4, y - arm + 2);
  ctx.restore();
}
